import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DisbursementEmailDialog from "@/components/DisbursementEmailDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Scale, Pencil, Plus, Trash2, Loader2, X, FileDown } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1";
const num = (v) => Number(v) || 0;

function preview(f) {
  const gross = num(f.gross_settlement);
  const fee = Math.round((f.fee_type === "flat" ? num(f.fee_flat) : gross * num(f.fee_percent) / 100) * 100) / 100;
  const costs = num(f.case_costs);
  const liensNet = f.liens.reduce((s, l) => s + Math.max(num(l.amount) - num(l.reduction), 0), 0);
  const other = f.other_deductions.reduce((s, o) => s + num(o.amount), 0);
  return { fee, costs, liensNet, other, net: Math.round((gross - fee - costs - liensNet - other) * 100) / 100, gross };
}

export function ClientDisbursement({ clientId, clientName, clientEmail }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/disbursement`).then((r) => setData(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setForm({
      gross_settlement: data.gross_settlement || 0, fee_type: data.fee_type || "percent",
      fee_percent: data.fee_percent ?? 33.33, fee_flat: data.fee_flat || 0,
      case_costs: data.case_costs || 0, liens: data.liens || [], other_deductions: data.other_deductions || [],
      notes: data.notes || "",
    });
    setEditing(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLien = (i, k, v) => set("liens", form.liens.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const setDed = (i, k, v) => set("other_deductions", form.other_deductions.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gross_settlement: num(form.gross_settlement), fee_type: form.fee_type,
        fee_percent: num(form.fee_percent), fee_flat: num(form.fee_flat), case_costs: num(form.case_costs),
        liens: form.liens.map((l) => ({ label: l.label, amount: num(l.amount), reduction: num(l.reduction) })),
        other_deductions: form.other_deductions.map((o) => ({ label: o.label, amount: num(o.amount) })),
        notes: form.notes,
      };
      const { data: updated } = await api.put(`/clients/${clientId}/disbursement`, payload);
      setData(updated);
      setEditing(false);
      toast.success("Disbursement saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save disbursement");
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/disbursement/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "disbursement.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not export statement");
    }
  };

  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-disbursement-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <Scale className="w-4 h-4 text-gray-400" /> Settlement Disbursement
        </h3>
        {!editing && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="disbursement-statement-button"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-[#002FA7] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
                  <FileDown className="w-3.5 h-3.5" /> Statement
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem data-testid="disbursement-download-pdf" onClick={downloadPdf}>Download PDF</DropdownMenuItem>
                <DropdownMenuItem data-testid="disbursement-email" onClick={() => setEmailOpen(true)}>Email statement…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={startEdit} data-testid="edit-disbursement-button"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div data-testid="disbursement-view">
          {!data.exists && t.gross === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-sm">
              No settlement recorded yet. Click <strong>Edit</strong> to build a disbursement statement.
            </p>
          ) : (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Gross settlement</span><span className="font-semibold text-gray-900">{fmt(t.gross)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Attorney fee{data.fee_type === "percent" ? ` (${Number(data.fee_percent)}%)` : ""}</span><span className="text-rose-600">({fmt(t.attorney_fee)})</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Case costs</span><span className="text-rose-600">({fmt(t.case_costs)})</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Medical liens (net)</span><span className="text-rose-600">({fmt(t.liens_net)})</span></div>
              {t.other_deductions_total > 0 && (
                <div className="flex justify-between"><span className="text-gray-600">Other deductions</span><span className="text-rose-600">({fmt(t.other_deductions_total)})</span></div>
              )}
              <div className="flex justify-between border-t border-gray-200 mt-2 pt-2">
                <span className="font-bold text-gray-900">Net to client</span>
                <span className="font-heading font-extrabold text-[#002FA7] text-lg" data-testid="net-to-client">{fmt(t.net_to_client)}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="disbursement-form">
          <div>
            <label className={labelCls}>Gross settlement</label>
            <input type="number" step="0.01" value={form.gross_settlement} onChange={(e) => set("gross_settlement", e.target.value)} className={inputCls} data-testid="disb-gross-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Fee type</label>
              <select value={form.fee_type} onChange={(e) => set("fee_type", e.target.value)} className={inputCls} data-testid="disb-fee-type">
                <option value="percent">Percentage</option>
                <option value="flat">Flat amount</option>
              </select>
            </div>
            {form.fee_type === "percent" ? (
              <div>
                <label className={labelCls}>Fee %</label>
                <input type="number" step="0.01" value={form.fee_percent} onChange={(e) => set("fee_percent", e.target.value)} className={inputCls} data-testid="disb-fee-percent" />
              </div>
            ) : (
              <div>
                <label className={labelCls}>Fee amount</label>
                <input type="number" step="0.01" value={form.fee_flat} onChange={(e) => set("fee_flat", e.target.value)} className={inputCls} data-testid="disb-fee-flat" />
              </div>
            )}
          </div>
          <div>
            <label className={labelCls}>Case costs</label>
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" value={form.case_costs} onChange={(e) => set("case_costs", e.target.value)} className={inputCls} data-testid="disb-case-costs" />
              {data.auto?.costs_total > 0 && (
                <button type="button" onClick={() => set("case_costs", data.auto.costs_total)} data-testid="disb-use-auto-costs"
                  className="text-xs font-semibold text-[#002FA7] whitespace-nowrap hover:underline">Use {fmt(data.auto.costs_total)}</button>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={labelCls}>Medical liens</label>
              <button type="button" onClick={() => set("liens", [...form.liens, { label: "", amount: 0, reduction: 0 }])}
                className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add lien</button>
            </div>
            <div className="space-y-2">
              {form.liens.length === 0 && <p className="text-xs text-gray-400">No liens.</p>}
              {form.liens.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={l.label} onChange={(e) => setLien(i, "label", e.target.value)} placeholder="Provider" className={`${inputCls} flex-1`} data-testid={`lien-label-${i}`} />
                  <input type="number" step="0.01" value={l.amount} onChange={(e) => setLien(i, "amount", e.target.value)} placeholder="Amount" className={`${inputCls} w-24`} data-testid={`lien-amount-${i}`} />
                  <input type="number" step="0.01" value={l.reduction} onChange={(e) => setLien(i, "reduction", e.target.value)} placeholder="Reduction" className={`${inputCls} w-24`} data-testid={`lien-reduction-${i}`} />
                  <button type="button" onClick={() => set("liens", form.liens.filter((_, idx) => idx !== i))} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={labelCls}>Other deductions</label>
              <button type="button" onClick={() => set("other_deductions", [...form.other_deductions, { label: "", amount: 0 }])}
                className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            <div className="space-y-2">
              {form.other_deductions.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={o.label} onChange={(e) => setDed(i, "label", e.target.value)} placeholder="Description" className={`${inputCls} flex-1`} data-testid={`ded-label-${i}`} />
                  <input type="number" step="0.01" value={o.amount} onChange={(e) => setDed(i, "amount", e.target.value)} placeholder="Amount" className={`${inputCls} w-28`} data-testid={`ded-amount-${i}`} />
                  <button type="button" onClick={() => set("other_deductions", form.other_deductions.filter((_, idx) => idx !== i))} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} data-testid="disb-notes" />
          </div>

          <div className="bg-[#002FA7]/5 rounded-sm p-3 flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700">Net to client (preview)</span>
            <span className="font-heading font-extrabold text-[#002FA7] text-lg" data-testid="net-preview">{fmt(preview(form).net)}</span>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setEditing(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-1.5"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-disbursement-button"
              className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </div>
      )}

      <DisbursementEmailDialog open={emailOpen} onOpenChange={setEmailOpen} clientId={clientId} clientName={clientName} clientEmail={clientEmail} />
    </div>
  );
}
